/**
 * Game-level systems on top of SimWorld: GlobalGameHandler (checkpoint load counter), CheckpointHandler
 * (snapshot save / restore), PlayerSpawner (spawn points, respawn index), LevelData, level actions
 * (coins, gems...), entity reference resolution and the material data table.
 */
import type { LevelDoc } from '../../model/format.ts';
import type { GameData } from '../../data/gameData.ts';
import { Behaviour, SimWorld, type PhysMat, type SimGo, type WorldSnapshot, MATERIALS } from './world.ts';
import { FrameCounter, rng, type RGBA, type V2 } from './unity.ts';
import type { CameraLookAt } from './camera.ts';
import type { PixelSource } from './pixels.ts';

// ---------- input ----------

export interface PlayerInput {
  movement: V2;
  jumpPressed: boolean;
  jumpReleased: boolean;
  dashPressed: boolean;
  interactPressed: boolean;
  interactDown: boolean;
  powerupPressed: boolean;
  powerupReleased: boolean;
  restartPressed: boolean;
  /** camera pan, or flight while controlEnabled (IJKL / right stick) */
  control: V2;
  /** DDown without control held: claim the camera */
  claimCameraPressed: boolean;
  /** Ctrl / left shoulder */
  controlEnabled: boolean;
  /** 0..1 */
  leftTrigger: number;
  /** 0..1 */
  rightTrigger: number;
  dUp: boolean;
  dDown: boolean;
  dLeft: boolean;
  dRight: boolean;
  /** Gamepad Start / P. Toggles pause. */
  pausePressed: boolean;
}

export function emptyInput(): PlayerInput {
  return {
    movement: { x: 0, y: 0 },
    control: { x: 0, y: 0 },
    claimCameraPressed: false,
    controlEnabled: false,
    leftTrigger: 0,
    rightTrigger: 0,
    jumpPressed: false,
    jumpReleased: false,
    dashPressed: false,
    interactPressed: false,
    interactDown: false,
    powerupPressed: false,
    powerupReleased: false,
    restartPressed: false,
    dUp: false,
    dDown: false,
    dLeft: false,
    dRight: false,
    pausePressed: false,
  };
}

// ---------- references ----------

export interface RefTarget {
  go: SimGo;
  /** component type name of the referenced node (GameObject / Transform when the object itself) */
  type: string;
  obj: unknown;
}

/** fileID -> object map of one prefab instance (internal references) */
export type RefCtx = Map<string, RefTarget>;

// ---------- material data (MaterialPropertyHandler on GlobalGameHandler) ----------

export interface ExtMaterialData {
  name: string;
  material: PhysMat | null;
  isDefault: boolean;
  characterAcceleration: number;
  characterAccelerationAgainst: number;
  characterDeceleration: number;
  rank: number;
  canWallJump: boolean;
}

export const MATERIAL_DATA = {
  default: { name: 'Default', material: null, isDefault: true, characterAcceleration: 1, characterAccelerationAgainst: 0.4, characterDeceleration: 0.5, rank: 0, canWallJump: true } as ExtMaterialData,
  ice: { name: 'Ice', material: MATERIALS.bf348de5212ce5842900bb6a43166a6f, isDefault: false, characterAcceleration: 0.5, characterAccelerationAgainst: 0.4, characterDeceleration: 0, rank: 1, canWallJump: false } as ExtMaterialData,
  air: { name: 'Air', material: MATERIALS.e10a03fda7eb02e489d829d9150f2092, isDefault: false, characterAcceleration: 0.2, characterAccelerationAgainst: 0.4, characterDeceleration: 0.1, rank: 1, canWallJump: true } as ExtMaterialData,
};

/** MaterialPropertyHandler.GetMaterialData (including its carry-over quirk for null materials) */
export function getMaterialData(materials: (PhysMat | null)[]): ExtMaterialData {
  if (!materials.length) return MATERIAL_DATA.air;
  let tryGet: ExtMaterialData | null = null;
  let lowest: ExtMaterialData | null = null;
  let lowestRank = Infinity;
  for (const m of materials) {
    if (m) tryGet = m.name === 'LevelIce' ? MATERIAL_DATA.ice : null;
    if (!tryGet) tryGet = MATERIAL_DATA.default;
    if (tryGet.rank < lowestRank) {
      lowestRank = tryGet.rank;
      lowest = tryGet;
    }
  }
  return lowest ?? MATERIAL_DATA.default;
}

// ---------- events for the view / HUD ----------

export type LevelActionKind = 'coin' | 'shard' | 'gem' | 'powerup' | 'breakable';

/** SoundType enum values, by index (Assets/Scripts/Sound/SoundType.cs) */
export const SOUND_TYPES = ['None', 'Jump', 'WallJump', 'Coin', 'Dash', 'Jetpack', 'Checkpoint', 'PlayerDeath', 'UIButton', 'Powerup', 'Cannon'] as const;

export type GameEvent =
  | { kind: 'death'; player: number; x: number; y: number; color: RGBA }
  | { kind: 'collect'; what: LevelActionKind; player: number; x: number; y: number }
  | { kind: 'checkpoint'; x: number; y: number }
  | { kind: 'restart' }
  | { kind: 'levelComplete' }
  | { kind: 'sound'; name: string; x: number; y: number };

// ---------- the game ----------

/** minimal player surface the game layer needs (implemented by PlayerController) */
export interface GamePlayer extends Behaviour {
  index: number;
  color: RGBA;
  input: PlayerInput;
  initialSpawn: V2 | null;
  totalDeaths: number;
  playerState: number;
  coinCount: number;
  gemCount: number;
  shardCount: number;
  respawnFresh(pos: V2): void;
  beginDeath(d: { primary: number; secondary?: string }): void;
}

export interface SpawnPointLike {
  go: SimGo;
  spawnOrder: number;
}

export interface CheckpointLike extends Behaviour {
  checkpointNumber: number;
}

export class Game extends SimWorld {
  readonly doc: LevelDoc;
  readonly gd: GameData;
  /** sprite alpha for runtime tilemap collider rebuilds */
  pixels: PixelSource | null = null;
  players: GamePlayer[] = [];
  spawnPoints: SpawnPointLike[] = [];
  respawnIndex = 0;
  /** editor "play from here" */
  spawnOverride: V2 | null = null;
  lastCheckpoint: CheckpointLike | null = null;
  currentCheckpoint: CheckpointLike | null = null;
  private ckSnapshot: WorldSnapshot | null = null;
  initialSnapshot: WorldSnapshot | null = null;
  lastCheckpointCounter = FrameCounter.seconds(2);
  restoreNextFrame = false;
  saveNextFrame = false;
  private restartPending = false;
  isInEndgame = false;
  /** Time.timeScale while running. Pause holds the simulation without changing this. */
  gameSpeed = 1;
  isPaused = false;
  pauseDebug = false;
  /** player index that opened the pause menu */
  pauseSlot = 0;
  /** Volleyball score for the HUD. Null when the level has no match. */
  volleyHud: { left: number; right: number } | null = null;
  /** PlayerController.OnAnyPlayerDied */
  onPlayerDied: ((p: GamePlayer) => void)[] = [];
  levelFinishedFrame = 0;
  /** entity ref base ("obj:id", "body:id", "layer:id") -> its GameObject (tilemap / rigidbody GO for bodies) */
  entities = new Map<string, SimGo>();
  /** entity -> root searched for "#Component" refs, when it differs from the entity GO (moving platform handlers) */
  entityRoots = new Map<string, SimGo>();
  levelMeta = { connectPlayersWithString: false, maxStringLength: 8, checkpointObject: null as SimGo | null };
  /** PlayerOverrides component data found in the level, if any */
  playerOverrides: Record<string, unknown> | null = null;
  events: GameEvent[] = [];
  /** factory set by the level builder */
  spawnPlayerFactory: ((index: number, color: RGBA) => GamePlayer) | null = null;
  readonly playerColors: RGBA[] = [
    [96 / 255, 1, 114 / 255, 1],
    [53 / 255, 145 / 255, 1, 1],
    [1, 151 / 255, 53 / 255, 1],
  ];
  /** parallax background prefab instance (rendered relative to the camera) */
  parallaxRoot: SimGo | null = null;
  /** cosmetic time (render frames) */
  renderTime = 0;
  /** viewport width / height, set by the view (CameraController sizing) */
  viewAspect = 16 / 9;
  camera: { focusOnPlayer(p: GamePlayer | null): void; setLookAtHandler(h: CameraLookAt): void; pos: V2; orthoSize: number } | null = null;
  /** deterministic UnityEngine.Random replacement */
  random = rng(12345);
  /** Instantiate(prefab, position, rotation) — set by the level builder */
  instantiateFactory: ((guid: string, pos: V2, rotation: number) => SimGo | null) | null = null;
  /** Player strings, after a join, restart, or checkpoint restore. Set by the level builder. */
  afterPlayersChanged: (() => void) | null = null;
  /** Entering a door turns off the strings tied to that player. */
  onEnterDoor: ((p: GamePlayer) => void) | null = null;

  constructor(doc: LevelDoc, gd: GameData) {
    super({ x: gd.project.physics?.gravity?.[0] ?? 0, y: gd.project.physics?.gravity?.[1] ?? -33 });
    this.doc = doc;
    this.gd = gd;
    this.beforeFixedUpdate = () => this.globalFixedUpdate();
    this.afterStep = () => {
      if (this.restartPending) {
        this.restartPending = false;
        this.doRestart();
      }
    };
  }

  emit(e: GameEvent) {
    this.events.push(e);
    if (this.events.length > 200) this.events.splice(0, this.events.length - 200);
  }

  // ---------- references ----------

  resolveRef(v: any, ctx: RefCtx | null): RefTarget | null {
    if (typeof v === 'string') return v ? this.resolveEntity(v) : null;
    if (!v || typeof v !== 'object') return null;
    const ent: string | undefined = v.entity ?? v.ref;
    if (typeof ent === 'string') return this.resolveEntity(ent);
    const fid = v.fileID === undefined ? null : String(v.fileID);
    if (!fid || fid === '0') return null;
    if (ctx) return ctx.get(fid) ?? null;
    return null;
  }

  resolveEntity(ent: string): RefTarget | null {
    const hash = ent.indexOf('#');
    const base = hash >= 0 ? ent.slice(0, hash) : ent;
    const type = hash >= 0 ? ent.slice(hash + 1) : 'GameObject';
    const go = this.entities.get(base);
    if (!go) return null;
    if (type === 'GameObject' || type === 'Transform' || type === 'Tilemap') return { go, type, obj: go };
    if (type === 'Rigidbody2D') {
      const g = go.descendants().find((x) => x.body);
      return g ? { go: g, type, obj: g.body } : null;
    }
    const root = this.entityRoots.get(base) ?? go;
    for (const g of root.descendants()) {
      for (const b of g.behaviours) if ((b as any).unityType === type) return { go: g, type, obj: b };
    }
    for (const g of root.descendants()) {
      const c = g.colliders.find((x) => x.type === type);
      if (c) return { go: g, type, obj: c };
    }
    return null;
  }

  // ---------- GlobalGameHandler / CheckpointHandler ----------

  private globalFixedUpdate() {
    if (this.lastCheckpointCounter.countCheckFinishReset()) this.restoreState();
  }

  startCheckpointLoadCounter() {
    this.lastCheckpointCounter.start();
  }

  /** False while a mode such as king of the hill handles death itself. */
  allowDeathCheckpoint(): boolean {
    for (const b of this.behaviours) {
      const block = (b as { blocksCheckpointLoad?: () => boolean }).blocksCheckpointLoad;
      if (typeof block === 'function' && block.call(b)) return false;
    }
    return true;
  }

  canPause(p: GamePlayer): boolean {
    for (const b of this.behaviours) {
      const ask = (b as { canPause?: (player: GamePlayer) => boolean }).canPause;
      if (typeof ask === 'function' && !ask.call(b, p)) return false;
    }
    return true;
  }

  togglePause(playerIndex: number, debug: boolean) {
    if (this.isPaused) {
      this.isPaused = false;
      return;
    }
    if (this.isInEndgame) return;
    const player = this.players[playerIndex];
    if (player && !this.canPause(player)) {
      this.emit({ kind: 'sound', name: 'uiError', x: player.go.position.x, y: player.go.position.y });
      return;
    }
    this.isPaused = true;
    this.pauseDebug = debug;
    this.pauseSlot = playerIndex;
  }

  /** Pause-menu command from the host, or relayed from a guest. */
  applyPause(cmd: string, n: number | undefined, hooks: { onExit: () => void; onCut: () => void }) {
    const player = this.players[this.pauseSlot];
    if (cmd === 'yes') return;
    if (cmd === 'resume') {
      this.isPaused = false;
      return;
    }
    if (cmd === 'restart' || cmd === 'dropout') {
      this.isPaused = false;
      this.restartLevel();
      return;
    }
    if (cmd === 'exit' || cmd === 'boot') {
      this.isPaused = false;
      hooks.onExit();
      return;
    }
    const givePowerup = (player as unknown as { givePowerup?: (t: number) => void } | null)?.givePowerup;
    if (cmd === 'powerup' && typeof givePowerup === 'function') {
      givePowerup(n ?? 0);
      this.isPaused = false;
      return;
    }
    if (cmd === 'speed') {
      const speed = n === 0.25 || n === 0.5 || n === 1 || n === 2 ? n : 1;
      this.gameSpeed = speed;
      this.isPaused = false;
      return;
    }
    if (cmd === 'invuln' && player && 'invulnerableToDeath' in player) {
      const pc = player as { invulnerableToDeath: boolean };
      pc.invulnerableToDeath = !pc.invulnerableToDeath;
      this.isPaused = false;
      return;
    }
    if (cmd === 'cut') {
      hooks.onCut();
      this.isPaused = false;
    }
  }

  /** G.S.HandleLastCheckpoint (restart button) */
  handleLastCheckpoint() {
    if (this.lastCheckpointCounter.isRunningNotFinished()) this.lastCheckpointCounter.markFinished();
    else for (const p of this.players) p.beginDeath({ primary: 8, secondary: 'LastCheckpointPressed' });
  }

  /** CheckpointHandler.SaveState. Refused while anyone is dying (PlayerState.Dying). */
  saveState(): boolean {
    if (this.players.some((p) => p.playerState === 1)) return false;
    this.saveNextFrame = true;
    return true;
  }

  restoreState(reloadSceneIfNoState = true) {
    this.lastCheckpointCounter.reset();
    if (this.ckSnapshot) this.restoreNextFrame = true;
    else if (reloadSceneIfNoState) this.restartLevel();
  }

  captureSnapshot(): WorldSnapshot {
    return this.snapshot();
  }

  applySnapshot(s: WorldSnapshot | null): boolean {
    if (!s) return false;
    if (this.ckSnapshot) this.liveSnapshots.delete(this.ckSnapshot);
    this.ckSnapshot = s;
    this.liveSnapshots.add(s);
    return true;
  }

  hasSavedState() {
    return !!this.ckSnapshot;
  }

  /** CheckpointHandler.FixedUpdate (execution order 10000) */
  checkpointHandlerFixedUpdate() {
    if (this.restoreNextFrame) this.actuallyRestore();
    else if (this.saveNextFrame) {
      const s = this.snapshot();
      this.applySnapshot(s);
    }
    this.restoreNextFrame = false;
    this.saveNextFrame = false;
  }

  private actuallyRestore() {
    if (!this.ckSnapshot) return;
    const keep = new Set<SimGo>();
    for (const p of this.players) for (const g of p.go.descendants()) keep.add(g);
    this.restoreKeeping(this.ckSnapshot, keep);
    for (const p of this.players) {
      if (!this.ckSnapshot.gos.has(p.go)) p.respawnFresh(this.getRespawnPoint(p));
      (p as any).afterCheckpointRestore?.();
    }
    this.afterPlayersChanged?.();
  }

  private restoreKeeping(s: WorldSnapshot, keep: Set<SimGo>) {
    this.restore(s, keep);
  }

  restartLevel() {
    this.restartPending = true;
  }

  private doRestart() {
    if (!this.initialSnapshot) return;
    if (this.ckSnapshot) this.liveSnapshots.delete(this.ckSnapshot);
    this.ckSnapshot = null;
    this.lastCheckpoint = null;
    this.currentCheckpoint = null;
    this.restoreNextFrame = this.saveNextFrame = false;
    this.lastCheckpointCounter.reset();
    this.isInEndgame = false;
    this.levelFinishedFrame = 0;
    const keep = new Set<SimGo>();
    for (const p of this.players) for (const g of p.go.descendants()) keep.add(g);
    this.restoreKeeping(this.initialSnapshot, keep);
    this.respawnIndex = 0;
    for (const p of this.players) p.initialSpawn = null;
    for (const p of this.players) p.respawnFresh(this.getRespawnPoint(p));
    this.afterPlayersChanged?.();
    this.emit({ kind: 'restart' });
  }

  /** take the snapshot used for full restarts (after level setup, before players) */
  captureInitial() {
    this.runStarts();
    this.initialSnapshot = this.snapshot(null, true);
    this.liveSnapshots.add(this.initialSnapshot);
  }

  // ---------- spawning ----------

  /**
   * Treat the editor spawn override as the player's feet and lift it until the
   * standing player box clears every solid collider (a tap on the ground lands inside it).
   */
  settleSpawnOverride(size: V2 = { x: 0.75, y: 1 }) {
    const p = this.spawnOverride;
    if (!p) return;
    const base = { x: p.x, y: p.y + size.y / 2 };
    for (let dy = 0; dy <= 40; dy += 0.05) {
      const c = { x: base.x, y: base.y + dy };
      if (!this.overlapBox(c, size, 0, (col) => !col.isTrigger).length) {
        this.spawnOverride = c;
        return;
      }
    }
    this.spawnOverride = base;
  }

  getRespawnPoint(p: GamePlayer): V2 {
    if (this.lastCheckpoint) return this.lastCheckpoint.go.position;
    if (p.initialSpawn) return { ...p.initialSpawn };
    if (this.spawnOverride) {
      p.initialSpawn = { ...this.spawnOverride };
      return { ...this.spawnOverride };
    }
    const sp = this.spawnPointFromIndex(this.respawnIndex++);
    const pos = sp ? sp.go.position : { x: 0, y: 2 };
    p.initialSpawn = { ...pos };
    return pos;
  }

  /** PlayerSpawner.GetFirstSpawnPoint (the editor spawn override wins) */
  firstSpawnPoint(): V2 | null {
    if (this.spawnOverride) return { ...this.spawnOverride };
    return this.spawnPointFromIndex(0)?.go.position ?? null;
  }

  instantiate(guid: string, pos: V2, rotation = 0): SimGo | null {
    return this.instantiateFactory?.(guid, pos, rotation) ?? null;
  }

  private spawnPointFromIndex(i: number): SpawnPointLike | null {
    const list = this.spawnPoints.filter((s) => !s.go.destroyed).slice();
    if (!list.length) return null;
    list.sort((a, b) => a.spawnOrder - b.spawnOrder);
    return list[i % list.length];
  }

  /** PlayerSpawner.OnPlayerJoined */
  joinPlayer(): GamePlayer | null {
    if (!this.spawnPlayerFactory) return null;
    const index = this.players.length;
    const color = this.playerColors[index % this.playerColors.length];
    const p = this.spawnPlayerFactory(index, color);
    this.players.push(p);
    this.afterPlayersChanged?.();
    if (this.frame > 0) this.emit({ kind: 'sound', name: 'playerJoin', x: p.go.position.x, y: p.go.position.y });
    return p;
  }

  // ---------- level actions ----------

  /** G.S.ActionPerformed -> PlayerController.OnActionPerformed (counts + score) */
  levelAction(kind: LevelActionKind, player: GamePlayer, at?: V2) {
    const p = player as GamePlayer & { score: number };
    switch (kind) {
      case 'coin': p.coinCount++; p.score += 100; break;
      case 'shard': p.shardCount++; p.score += 200; break;
      case 'gem': p.gemCount++; p.score += 1000; break;
      case 'powerup': p.score += 500; break;
      case 'breakable': p.score += 25; break;
    }
    const pos = at ?? player.go.position;
    this.emit({ kind: 'collect', what: kind, player: player.index, x: pos.x, y: pos.y });
  }

  /** G.S.ShowEndGameUI */
  showEndGame() {
    if (this.isInEndgame) return;
    this.isInEndgame = true;
    this.levelFinishedFrame = this.frame;
    this.emit({ kind: 'levelComplete' });
  }

  /** PlayerController.OnAnyPlayerDash subscribers */
  dashListeners = new Set<{ go: SimGo; onPlayerDash(p: GamePlayer): void }>();
  /** PlayerController.OnAnyPlayerJumped subscribers (player, isWallJump) */
  jumpListeners = new Set<{ go: SimGo; onPlayerJumped(p: GamePlayer, isWallJump: boolean): void }>();

  /** frame step with per-player inputs. A pause press freezes the step. */
  tick(inputs: PlayerInput[]) {
    let toggled = false;
    this.players.forEach((p, i) => {
      const input = inputs[i] ?? emptyInput();
      p.input = input;
      if (!toggled && input.pausePressed) {
        toggled = true;
        this.togglePause(i, input.controlEnabled);
      }
    });
    if (this.isPaused) return;
    for (const p of this.players) if (p.input.claimCameraPressed && !p.input.controlEnabled && p.input.leftTrigger <= 0) this.camera?.focusOnPlayer(p);
    this.step();
  }
}

/** CheckpointHandler.FixedUpdate runs at script execution order 10000, after every other script. */
export class CheckpointHandlerBehaviour extends Behaviour {
  static override order = 10000;
  override fixedUpdate() {
    (this.go.world as unknown as Game).checkpointHandlerFixedUpdate();
  }
}

/** Base class of game behaviours: typed access to the game and reference resolution. */
export abstract class GameBehaviour extends Behaviour {
  /** Unity component type name (set by the builder) */
  unityType = '';
  ctx: RefCtx | null = null;
  get game(): Game {
    return this.go.world as unknown as Game;
  }
  ref(v: any): RefTarget | null {
    return this.game.resolveRef(v, this.ctx);
  }
  refBehaviour<T extends Behaviour>(v: any, cls: abstract new (...a: any[]) => T): T | null {
    const r = this.ref(v);
    if (!r) return null;
    if (r.obj instanceof cls) return r.obj as T;
    return r.go.getBehaviour(cls);
  }
  refGo(v: any): SimGo | null {
    return this.ref(v)?.go ?? null;
  }
  /** a sound event (src/audio/events.ts) at this object, or at `at` */
  sound(name: string, at?: V2) {
    const p = at ?? this.go.position;
    this.game.emit({ kind: 'sound', name, x: p.x, y: p.y });
  }
}
